import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEqual, pick } from "lodash";
import { useMemo, useState } from "react";

import { AddressData, addressKeys } from "@caredna-marlin/utils/client/geocoding";
import AddressForm from "~/components/common/forms/AddressForm";
import { OrganizationalUnitsQuery, UpdateErrandLocationMutation } from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";

type Location = {
  id: number;
  name: string;
  description: string;
  organizationalUnitId: number | null;
  addressStreet?: string | null;
  addressLineTwo?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  addressCoordinates?: string | null;
};

type Props = {
  errandTypeId: number;
  organizationalUnits: OrganizationalUnitsQuery["organizationalUnit"];
  location?: Location;
  onCancelDraft?: () => void;
  refetchQueries?: string[];
};

type LocationMode = "custom" | "visit";
type EditableLocation = AddressData & {
  name: string;
  description: string;
  organizationalUnitId?: number | null;
};
type SavedLocation = NonNullable<UpdateErrandLocationMutation["updateErrandLocation"]>;

const persistedAddressKeys = ["addressStreet", "addressLineTwo", "addressCity", "addressState", "addressZip"] as const;

const emptyPersistedAddress = {
  addressStreet: null,
  addressLineTwo: null,
  addressCity: null,
  addressState: null,
  addressZip: null,
};

const modeForAddress = (address: AddressData): LocationMode =>
  address.addressStreet || address.addressCoordinates ? "custom" : "visit";

const persistedAddress = (address: AddressData, mode: LocationMode) =>
  mode === "custom" ? pick(address, persistedAddressKeys) : emptyPersistedAddress;

const ErrandLocationForm = ({
  errandTypeId,
  organizationalUnits,
  location,
  onCancelDraft,
  refetchQueries = ["ErrandLocations"],
}: Props) => {
  const base = useMemo<EditableLocation>(() => ({
    name: location?.name ?? "",
    description: location?.description ?? "",
    organizationalUnitId: location?.organizationalUnitId,
    addressStreet: location?.addressStreet ?? null,
    addressLineTwo: location?.addressLineTwo ?? null,
    addressCity: location?.addressCity ?? null,
    addressState: location?.addressState ?? null,
    addressZip: location?.addressZip ?? null,
    addressCoordinates: location?.addressCoordinates ?? null,
    addressPlaceId: null,
  }), [location]);
  const initialMode: LocationMode = location ? modeForAddress(base) : "custom";
  const [local, setLocal] = useState(base);
  const [mode, setMode] = useState<LocationMode>(initialMode);
  // AddressForm keeps its own input state, so it needs a remount to show an
  // address that the server rewrote or cleared.
  const [addressFormKey, setAddressFormKey] = useState(0);
  const { errandTypes } = useOrgConfig();
  const errandTypeName = errandTypes[errandTypeId]?.name ?? "this";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [create, createState] = useMutation(CREATE_ERRAND_LOCATION);
  const [update, updateState] = useMutation(UPDATE_ERRAND_LOCATION);
  const loading = createState.loading || updateState.loading;
  const activeOrganizationalUnits = organizationalUnits.filter(unit => unit.active);
  const organizationalUnitId = local.organizationalUnitId
    ?? (activeOrganizationalUnits.length === 1 ? activeOrganizationalUnits[0].id : null);
  const organizationalUnitLabel = organizationalUnits.length ? "Organizational Unit" : "Organizational Unit (not configured)";

  const applySaved = (saved: SavedLocation) => {
    setLocal({
      name: saved.name,
      description: saved.description,
      organizationalUnitId: saved.organizationalUnitId,
      addressStreet: saved.addressStreet ?? null,
      addressLineTwo: saved.addressLineTwo ?? null,
      addressCity: saved.addressCity ?? null,
      addressState: saved.addressState ?? null,
      addressZip: saved.addressZip ?? null,
      addressCoordinates: saved.addressCoordinates ?? null,
      addressPlaceId: null,
    });
    setMode(modeForAddress(saved));
    setAddressFormKey(key => key + 1);
  };

  const addressChanged = !isEqual(
    persistedAddress(local, mode),
    persistedAddress(base, initialMode),
  );

  const save = async (updateFutureResources: boolean) => {
    const address = mode === "custom"
      ? pick(local, addressKeys)
      : {
          addressStreet: null,
          addressLineTwo: null,
          addressCity: null,
          addressState: null,
          addressZip: null,
          addressPlaceId: null,
        };
    const variables = {
      name: local.name.trim(),
      description: local.description,
      organizationalUnitId,
      ...address,
    };
    const options = {
      awaitRefetchQueries: true,
      refetchQueries,
    };

    if (location) {
      const { data } = await update({
        variables: { id: location.id, updateFutureResources, ...variables },
        ...options,
      });
      if (data?.updateErrandLocation) applySaved(data.updateErrandLocation);
    } else {
      const { data } = await create({
        variables: { errandTypeId, ...variables },
        ...options,
      });
      if (data?.createErrandLocation) applySaved(data.createErrandLocation);
    }
    setConfirmOpen(false);
    onCancelDraft?.();
  };

  const onSave = () => {
    if (location && addressChanged) {
      setConfirmOpen(true);
      return;
    }
    return save(false);
  };

  const dirty = !location || mode !== initialMode || !isEqual(local, base)
    || organizationalUnitId !== base.organizationalUnitId;
  const valid = !!local.name.trim()
    && (organizationalUnitId != null || activeOrganizationalUnits.length === 0)
    && (mode === "visit" || !!local.addressStreet);

  return (
    <Grid container spacing={2} sx={{ pt: 1 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Name"
          value={local.name}
          onChange={event => setLocal({ ...local, name: event.target.value })}
          disabled={loading}
          required
          fullWidth
          size="small"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth required={activeOrganizationalUnits.length > 0} size="small" disabled={loading || organizationalUnits.length === 0}>
          <InputLabel id={`errand-location-unit-${location?.id ?? "new"}`}>{organizationalUnitLabel}</InputLabel>
          <Select<number | "">
            labelId={`errand-location-unit-${location?.id ?? "new"}`}
            label={organizationalUnitLabel}
            value={organizationalUnitId ?? ""}
            onChange={event => setLocal({ ...local, organizationalUnitId: Number(event.target.value) })}
          >
            {organizationalUnits.map(unit => (
              <MenuItem key={unit.id} value={unit.id} disabled={!unit.active && unit.id !== organizationalUnitId}>
                {unit.name}{!unit.active ? " (retired)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={12}>
        <TextField
          label="Description"
          value={local.description}
          onChange={event => setLocal({ ...local, description: event.target.value })}
          disabled={loading}
          multiline
          minRows={2}
          fullWidth
          size="small"
        />
      </Grid>
      <Grid size={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Location Type</Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, value: LocationMode | null) => {
            if (!value) return;
            setMode(value);
          }}
          aria-label="Location Type"
        >
          <ToggleButton value="custom">Custom Location</ToggleButton>
          <ToggleButton value="visit">At Visit Location</ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Grid size={12} sx={{ display: mode === "visit" ? "none" : undefined }}>
        <AddressForm
          key={addressFormKey}
          address={pick(local, addressKeys)}
          setAddress={address => setLocal({ ...local, ...address })}
          disabled={loading}
          required={mode === "custom"}
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid size={12}>
        <Button
          variant="contained"
          size="small"
          disabled={!dirty || !valid}
          loading={loading}
          onClick={onSave}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          sx={{ ml: 1 }}
          onClick={() => {
            setLocal(base);
            setMode(initialMode);
            if (!location) onCancelDraft?.();
          }}
        >
          {location ? "Reset" : "Cancel"}
        </Button>
      </Grid>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent>
          <DialogContentText>
            {`Also update "${errandTypeName}" errands scheduled in the future with this address to use new address?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => save(false)} disabled={loading}>No</Button>
          <Button onClick={() => save(true)} variant="contained" loading={loading}>Yes</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ErrandLocationForm;

const CREATE_ERRAND_LOCATION = gql(`
  mutation CreateErrandLocation(
    $errandTypeId: Int!
    $organizationalUnitId: Int
    $name: String!
    $description: String!
    $addressStreet: String
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $addressPlaceId: String
  )
  @permission(requires: manageOrgConfig)
  {
    createErrandLocation(
      errandTypeId: $errandTypeId
      organizationalUnitId: $organizationalUnitId
      name: $name
      description: $description
      addressStreet: $addressStreet
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressPlaceId: $addressPlaceId
    ) {
      id
      organizationalUnitId
      name
      description
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressCoordinates
    }
  }
`);

const UPDATE_ERRAND_LOCATION = gql(`
  mutation UpdateErrandLocation(
    $id: Int!
    $organizationalUnitId: Int
    $name: String!
    $description: String!
    $updateFutureResources: Boolean
    $addressStreet: String
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $addressPlaceId: String
  )
  @permission(requires: manageOrgConfig)
  {
    updateErrandLocation(
      id: $id
      organizationalUnitId: $organizationalUnitId
      name: $name
      description: $description
      updateFutureResources: $updateFutureResources
      addressStreet: $addressStreet
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressPlaceId: $addressPlaceId
    ) {
      id
      organizationalUnitId
      name
      description
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressCoordinates
    }
  }
`);
